import { useEffect, useMemo, useRef, useState } from "react";
import { useDraft } from "../../hooks/useDraft";
import { DraftRefusedError, type DraftRefusal, useSubmitAnswer } from "../../hooks/useQueues";
import { readReopenedRecord, reopenedReadingLine } from "../../lib/exploratoryReading";
import { rootNameOf } from "../../lib/displayName";
import { stripAnchors } from "../../lib/stripAnchors";
import { KEY_HINT_INLINE, shortcut } from "../../lib/platformKeys";
import {
  CONFIRM_PICK,
  CROSSING_HEADING,
  EDIT_ACT,
  NEEDS_HEADING,
  SAVED_WORD,
  crossingDecisions,
  obligationLabelText,
  provenanceLines,
  scopeWhyLine,
  titleNamesCitedDecision,
  whyWeAreAskingLine,
  type ReviewTreeMeta,
} from "../../lib/reviewLines";
import { AnswerBlock, ConfirmYesButton, isOptionText, routingPickOf } from "./AnswerBlock";
import { AnswerOption } from "./AnswerOption";
import { DetailsFold } from "./DetailsFold";
import { AuthorityForkPanel, splitAuthorityPicks } from "./AuthorityCard";
import { CoveredByHolderRow, useCoveredRowHolder } from "./CoveredByHolderRow";
import { WhyLine } from "./WhyLine";
import {
  NavFooter,
  asString,
  cardKeyAct,
  isTypingTarget,
  pickText,
  readPicks,
  type HandOffState,
  type Pick,
} from "./shared";
import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];

// ---- Question panel -------------------------------------------------

// 0074: the one-tap Yes replaces the answer with the confirm option's text, so
// it is held while the card holds any other text - an unsaved edit (typed, or
// restored from this browser), another option chosen, or a saved answer in the
// box. A blank box, or one holding exactly that text, loses nothing.
export function confirmYesHeldByEdit(localAnswer: string, confirmText: string): boolean {
  return localAnswer.trim() !== "" && !isOptionText(localAnswer, confirmText);
}

// Exported for the 0085 spec: one click selects an option, and selecting
// writes `pick.value || pick.label` verbatim as the draft (with the pick's
// routing id), which Save & next posts; Edit opens that draft in the box. That
// IS the design's choice-identity mechanism — the chosen option's full
// commitment text becomes the founder's answer and flows verbatim to the
// engine. A regression to pick.label would silently gut the answer; the spec
// pins it.
export function QuestionPanel({
  productId,
  node,
  nodeById,
  pos,
  onPrev,
  onNext,
  onActNext = onNext,
  awaitsCoveredByRuling = false,
  hasSavedDraft = false,
  answerUnderReview = false,
  mountedByDataSwap = false,
  onRefused = () => {},
  treeMeta,
  actsHeld = false,
  blocksContinue = true,
  handOff = "unknown",
  nothingWaits = false,
}: {
  productId: string;
  node: Node;
  nodeById: Map<string, Node>;
  pos: number;
  onPrev: () => void;
  // The founder's own forward move (skip →, the right arrow).
  onNext: () => void;
  // 0051 S1c: the forward move an act causes - the success path of Save & next,
  // the authority fork's save, the covered-by row's Yes and the confirm
  // option's Yes calls it, so the page knows the move was an act's (its
  // keyboard guard holds the next card's key acts). Default onNext: a card
  // rendered without the page's act-advance moves on as its own forward move
  // does.
  onActNext?: () => void;
  // 0148 A27: the SERVER's answer - node.id is in the questions queue's
  // served covered_by_ruling_node_ids (models.QuestionAwaitsCoveredByRuling
  // over the same draft-overlaid tree the queue serves).
  awaitsCoveredByRuling?: boolean;
  // 0148 A26: node.id is in the questions queue's drafted_node_ids.
  hasSavedDraft?: boolean;
  // 0051 S1a: node.id is in the questions queue's served
  // answers_under_review_node_ids (the server holds its answer under review).
  answerUnderReview?: boolean;
  // 0148 A40: this panel was mounted because the anchored node LEFT the queue
  // (a data swap, not the founder's navigation).
  mountedByDataSwap?: boolean;
  // 0159 F8: adds a refused save to the Review page's notice - the onRefused
  // option of every submit hook this panel renders (its own, the AUTHORITY
  // fork's, and the covered-by row's, whose Yes the server can refuse), each
  // with this question as its item, so any refusal the panels keep off their
  // inline line reaches the notice, carrying this question's id and title (and,
  // from the row, its holder's title).
  onRefused?: (refusal: DraftRefusal) => void;
  // 0051 S1a, S1c: the served meta the Why line reads (where the question sits)
  // and the product's name its anchors to the root read as.
  treeMeta?: ReviewTreeMeta;
  // 0051 S1a (F3-2): the page's mount gate - the queue was served from cache
  // and its mount fetch has not settled. Holds Save & next and its keystroke,
  // and the confirm option's Yes; the authority fork and the covered-by row
  // are held whole by their disabled fieldset. The ordinary card's selection
  // (AnswerOption, Edit, Write my own) and navigation are never held.
  actsHeld?: boolean;
  // 0051 S1b: node.id is in the questions queue's served
  // trigger_blocking_node_ids. Default true: without the page's served set the
  // card never claims it does not hold up Continue.
  blocksContinue?: boolean;
  // 0051 S1b: whether the spec is handed off, from the root's served copy:
  // "open" (not confirmed), "done" (confirmed) or "unknown" (no root served).
  // Default "unknown": without the served root the card never claims the
  // hand-off waits on it.
  handOff?: HandOffState;
  // 0051 S1c: nothing waits on the founder in either queue (the page's forward
  // rule). Default false: a card rendered without the page's served sets never
  // reads "done".
  nothingWaits?: boolean;
}) {
  const fields = (node.fields ?? {}) as Record<string, unknown>;
  // 0148 A40: a panel a data swap mounted holds its keystroke ACTS (here the
  // Cmd/Ctrl+Enter submit) until the founder clicks inside it or navigates.
  // Captured ONCE at mount, so the anchor's re-recording after the swap can
  // neither lift nor re-arm it; a navigation mounts a fresh panel (keyed on
  // node.id) with a fresh capture.
  const [keyActsHeld, setKeyActsHeld] = useState(mountedByDataSwap);

  // V2 structured-question: prose lives in three optional fields read
  // top-to-bottom. The `question` field is the founder-facing ask (always
  // present, always ends with `?`); `playback` is system-playback context
  // above the question; `hypothesis` is the system-proposed answer below
  // the picks. Picks live exclusively in `parsed_picks` (canonical-source —
  // V1's inline <pick> markup retired). Anchors may appear inline within
  // any prose field; rendered as plain text labels (stripAnchors), an anchor
  // to the root whose label is the whole idea as the product's name
  // (rootNameOf; 0169).
  const playbackRaw = asString(fields.playback);
  const questionRaw = asString(fields.question);
  const hypothesisRaw = asString(fields.hypothesis);
  const rootName = rootNameOf(treeMeta);
  const playback = stripAnchors(playbackRaw, rootName);
  const questionText = stripAnchors(questionRaw, rootName);
  const hypothesis = stripAnchors(hypothesisRaw, rootName);
  const headingText = questionText || node.title;
  // A default amend carrier's title is a machine template naming the target's
  // id (titleNamesCitedDecision): never printed as the label.
  const showTitleAsLabel =
    !!questionText &&
    node.title &&
    node.title !== questionText &&
    !titleNamesCitedDecision(node, nodeById);

  const servedPicks = useMemo(() => readPicks(fields.parsed_picks), [fields.parsed_picks]);
  // 0074: the engine's confirm option is this card's one-tap Yes, never one of
  // its options; every rule below reads the other options as before, except
  // the routing id Save & next posts and when the box opens, which read the
  // served ones (the confirm option's text is an answer the card knows).
  const confirmPick = servedPicks.find((p) => p.pickId === CONFIRM_PICK);
  const picks = useMemo(() => servedPicks.filter((p) => p.pickId !== CONFIRM_PICK), [servedPicks]);

  // 0129-T7: an AUTHORITY reconciliation question is marked by
  // fields.authority_conformance_dimension ("scope" | "moat") — the canonical
  // signal the engine stamps (planner.AuthorityConformanceDimensionField). It
  // gets a purpose-built fork (scope: Both stand, Keep, Revise, less Both stand
  // on a labelled question awaiting the covered-by ruling; moat: Keep, Revise)
  // instead of the generic
  // one-click pick (AnswerOption): the pick VALUES here are sentinels (or,
  // for a moat KEEP, the current value-core text), never a founder answer, so
  // dumping them into the answer box would be wrong.
  const authorityDimension = asString(fields.authority_conformance_dimension);
  const isAuthorityReconciliation =
    authorityDimension === "scope" || authorityDimension === "moat";
  // 0168 R-j: the dimension half of the one offer predicate
  // (planner.AuthorityBothStandOffered), T31-pinned; bothStandHidden below is the rest.
  const bothStandOffered = authorityDimension === "scope";
  // 0168 FD-4: a collision-born question (planner.EntailedObligationLabelOf,
  // trimmed) routes "both stand" to the 0146 T6 ruling arm, and its row says so.
  const isCollision = asString(fields.entailed_obligation_label).trim() !== "";
  const outOfScopeEntry = stripAnchors(asString(fields.out_of_scope_entry), rootName);
  // 0168 OQ6 (ruled 2026-10-06, "Hide it everywhere"): on a labelled scope
  // question served as awaiting the covered-by ruling, both stand re-sends the
  // answer whose ruling CREATE has dropped under the holder's title on every
  // pass that composed it (expected to loop), so the panel hides the row. It is
  // the served-state form of the server's one offer predicate (0168 R-j,
  // amended 2026-10-06; the server also accepts a changed answer,
  // planner.AuthorityBothStandOffered, 0168 R-j as amended). When the served
  // marker clears, the row comes back.
  const bothStandHidden = bothStandOffered && isCollision && awaitsCoveredByRuling;
  const { keepPick, revisePick, bothStandPick } = useMemo(
    () => splitAuthorityPicks(picks),
    [picks],
  );

  // Initialize draft from fields.answer (server-saved). After Save & Next
  // the localStorage entry is cleared; on Previous-back the queue refetch
  // gives us node.fields.answer = the just-saved value, which the hook
  // uses as initial. Net result: answers persist round-trip.
  const serverAnswer = asString(fields.answer);
  // Skip the third tuple slot (`clear`) — we use `commit` on save
  // instead of `clearDraft`, so localStorage retains the saved value
  // as a typing-time safety net across the brief queue-refetch window.
  const [draft, setDraft, , commit] = useDraft(productId, node.id, serverAnswer);
  const trimmed = draft.trim();
  // 0051 S1a, 0074: the one test that the draft IS an option - isOptionText,
  // the option's text (pickText) against the draft, both trimmed. The routing
  // id Save & next posts, each option's selected state, the Yes's chosen state
  // and boxOpen all read it.
  const draftIsPick = (p: Pick) => isOptionText(draft, pickText(p));
  const submit = useSubmitAnswer(productId, { item: node, onRefused });
  // The Yes's own post is out (its label and inert state; Save & next keeps its own).
  const [yesPosting, setYesPosting] = useState(false);

  // 0148 THE OFFER (A27, amended by 0051 S1c): the covered-by row is offered
  // only for a question the server serves as awaiting the founder's ruling -
  // the server's one function decides - and only when the page's map holds the
  // holder its served marker names (a render precondition: the row plays back
  // the holder's title and value). The stale-queue guard (a second, tree-query
  // copy of the question had to carry the same marker) retired with Review's
  // /tree read, a 0148 amendment: the questions queue serves the marker and the
  // holder in one response, so the skew between two separately refetched
  // queries it caught cannot occur; the mount gate holds a cached queue's acts
  // until its first fetch settles; and a COVERED click from a stale tab is
  // refused at the post (409 draft.covered_by_refused) or at S1 (0148 N2 as
  // amended). No kind branch (A6).
  const coveredByCandidateId = asString(fields.covered_by_candidate_id);
  const holder = nodeById.get(coveredByCandidateId);
  const offered =
    awaitsCoveredByRuling && coveredByCandidateId !== "" && holder !== undefined ? holder : undefined;
  const covered = useCoveredRowHolder(offered);

  // 0157 F4: a reopened question (its reading's disposition "reopened", with a
  // non-blank recorded reason) says why it came back, above its prefilled
  // answer. The title is the record's, else the served title of the recorded
  // decision; when neither names it, the line is not rendered (the answer is
  // still prefilled, and the reason is still on the rejected decision).
  const reopened = readReopenedRecord(fields);
  const reopenedTitle =
    reopened === null
      ? ""
      : reopened.decisionTitle.trim() !== ""
        ? reopened.decisionTitle
        : (nodeById.get(reopened.decisionId)?.title ?? "");

  const reasoningChain = stripAnchors(asString(fields.reasoning_chain), rootName);
  // 0085 design-first: a designed fork carries the axis its options differ on
  // (rendered as a label above the picks) and an optional flip condition (the
  // assumption that, if wrong, changes the answer — rendered under the picks).
  const decisionAxis = stripAnchors(asString(fields.decision_axis), rootName);
  const flipCondition = stripAnchors(asString(fields.flip_condition), rootName);

  const canSubmit = trimmed.length > 0 && !submit.isPending;

  // 0051 S1a: one click selects an option and nothing opens. The answer box
  // shows when there are no options, when the founder opened it (Edit or
  // Write my own), or when the draft is not an option's exact text (a saved
  // or restored free-text answer); a draft that IS an option's text shows
  // that option selected with the box closed, and one that is the confirm
  // option's text shows the Yes chosen with the box closed (0074: a Yes saved
  // and visited again reads as the choice, not as typed text).
  const [editorOpen, setEditorOpen] = useState(false);
  const boxOpen = picks.length === 0 || editorOpen || (trimmed !== "" && !servedPicks.some(draftIsPick));
  // Focus moves into the box only on the founder's Edit or Write my own click
  // (a ref and an effect on that request); nothing focuses it on mount.
  const answerBoxRef = useRef<HTMLTextAreaElement>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  useEffect(() => {
    if (focusRequest > 0) answerBoxRef.current?.focus();
  }, [focusRequest]);
  // The draft holds exactly the server's (non-empty) answer - the one text
  // test the saved state reads.
  const matchesSaved = serverAnswer.trim() !== "" && trimmed === serverAnswer.trim();
  // The one saved state: the server holds this question's save (its id is in
  // the served drafted or under-review set) and the draft is exactly it. An
  // undrafted reopened question, or an undrafted one awaiting the founder's
  // covered-by ruling, keeps its answer but is in neither served set, so it
  // shows the save hint (a drafted one of either kind is in the drafted set
  // and shows Saved).
  const saved = (hasSavedDraft || answerUnderReview) && matchesSaved && !submit.isPending;

  const handleSubmit = () => {
    if (actsHeld) return;
    if (!canSubmit) return;
    // 0131-T7, 0074: the structured pick id Save & next posts as selected_pick
    // (WriteAnswers routes on it), read at submit from the draft itself and never
    // kept beside it: when the draft is a routing pick's text (draftIsPick; the
    // delegation's - a settle-by-delegation act - or the confirm option's), that
    // pick's id; any other text carries none. So an answer that reads as a
    // routing pick always posts with its id, however the text reached the draft
    // (the option chosen, a draft restored from this browser on a remount, the
    // text typed, or an edit changed back), and an edited one never mis-routes (a
    // free-text enumeration answer never submits as a delegation). It reads the
    // served options, the confirm option included.
    submit.mutate(
      { questionId: node.id, answer: trimmed, selectedPick: routingPickOf(servedPicks, draft) || undefined },
      {
        onSuccess: () => {
          // Synchronously mirror the posted value into localStorage so
          // it survives the imminent panel unmount (Save & Next flips
          // pos -> key={node.id} change -> unmount, which would
          // otherwise cancel the typing-time debounced setItem).
          commit(trimmed);
          onActNext();
        },
      },
    );
  };

  // 0074: the confirm option's one-tap Yes - one act that saves the option's
  // text and moves on, held (confirmYesHeldByEdit) while the card holds any
  // other text.
  const confirmText = confirmPick ? pickText(confirmPick) : "";
  const yesHeld = confirmPick !== undefined && confirmYesHeldByEdit(draft, confirmText);
  // The card's answer is the confirm option's text (a Yes saved and visited
  // again, or that text restored or typed): the Yes shows as the chosen
  // answer, as a picked option does.
  const yesChosen = confirmPick !== undefined && draftIsPick(confirmPick);
  const onYes = () => {
    if (!confirmPick || actsHeld || yesHeld || yesPosting || submit.isPending) return;
    setYesPosting(true);
    submit.mutate(
      // The option's text verbatim (its value, else its label) with its routing
      // id: the one shape the engine reads as a confirmation of the read shown.
      { questionId: node.id, answer: confirmText, selectedPick: CONFIRM_PICK },
      {
        // Like Save & next: mirror the saved text locally, then move on as an act.
        // A failed post does neither; the card's error line says why.
        onSuccess: () => {
          commit(confirmText);
          onActNext();
        },
        onSettled: () => setYesPosting(false),
      },
    );
  };

  // Local: Cmd/Ctrl+Enter saves; plain ←/→ navigate when textarea is
  // NOT focused (so caret movement still works inside the answer). The
  // global Escape + Cmd/Ctrl+Arrow handlers live on Review-level above. The
  // act is read through cardKeyAct (shared.tsx), the one spelling Review's
  // keyboard guard reads too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inText = isTypingTarget(e);
      if (cardKeyAct(e) === "submit") {
        e.preventDefault();
        // Authority reconciliations submit through AuthorityForkPanel, which
        // owns its own Cmd/Ctrl+Enter handler; don't fire the generic path.
        // 0148 A40: a data-swapped panel skips the act until a click inside it.
        // 0051 S1a: so does a card whose queue's mount fetch is still out.
        if (!isAuthorityReconciliation && !keyActsHeld && !actsHeld) handleSubmit();
      } else if (!inText && e.key === "ArrowRight") {
        e.preventDefault();
        onNext();
      } else if (!inText && e.key === "ArrowLeft" && pos > 0) {
        e.preventDefault();
        onPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // 0051 S1b: why the card asks. A scope question names its out-of-scope line
  // and the decisions it lists (0170's list, titles from the served context) in one
  // of two forms (ruled 2026-10-06): a collision-born question names the
  // obligation those decisions need (they are its premises, 0146 T6); any other
  // names the decisions that cross the line. Every other question gives its
  // summary. Details lists what the question builds on, leaving out that list.
  const scopeLine = authorityDimension === "scope" ? outOfScopeEntry.trim() : "";
  const crossing = scopeLine !== "" ? crossingDecisions(node, nodeById) : [];
  const obligationLabel = scopeLine !== "" && isCollision ? obligationLabelText(node) : "";
  const whyLine =
    scopeLine !== ""
      ? scopeWhyLine(node, treeMeta, crossing.length, obligationLabel)
      : whyWeAreAskingLine(node, treeMeta);
  const crossingHeading = obligationLabel !== "" ? NEEDS_HEADING : CROSSING_HEADING;
  const buildsOn = provenanceLines(node, nodeById, new Set(crossing.map((c) => c.id))).buildsOn;

  // 0051 S1b: on an ordinary card with options, the covered-by row and the
  // reopened line sit above the options and the answer block directly under
  // them; on any other card the order is today's. Each element is built once.
  const optionsFirst = picks.length > 0 && !isAuthorityReconciliation;
  const coveredRow =
    covered.holder && !isAuthorityReconciliation ? (
      <fieldset disabled={actsHeld} className="m-0 min-w-0 border-0 p-0">
        <CoveredByHolderRow
          key={covered.holder.id}
          productId={productId}
          node={node}
          holder={covered.holder}
          localAnswer={draft}
          // The saved answer goes back; the routing id it posts with is read
          // from its own text at submit, so a restored routing-pick draft's id
          // never rides on it.
          onUseSavedAnswer={() => setDraft(serverAnswer)}
          onActNext={onActNext}
          hasSavedDraft={hasSavedDraft}
          amended={covered.amended}
          onAmendSaved={covered.onAmendSaved}
          onRefused={onRefused}
        />
      </fieldset>
    ) : null;
  const reopenedLine =
    reopened !== null && reopenedTitle.trim() !== "" ? (
      <p
        data-testid="reopened-reading-line"
        className="mt-6 break-words rounded-md border border-ink-700 bg-ink-900/60 px-4 py-3 text-sm text-ink-200"
      >
        {reopenedReadingLine(reopenedTitle, reopened.reason)}
      </p>
    ) : null;
  // 0074: the confirm option's Yes, built once: first in the options block on
  // a card with options, directly above the answer block on a card with none,
  // so it sits under the read and the question and above every other way to
  // answer. It reads the served label. Held over an edit it is hidden in its
  // slot (invisible: not shown, not focusable, and nothing moves); the mount
  // gate and another post of the card dim it in place, as they do Save & next.
  // While its own post is out it reads saving… and stays focusable
  // (aria-disabled, never disabled), so a retry after a failure is one Enter.
  // Chosen (the card's answer is its text) it shows as a picked option does:
  // pressed, with the selected ring; Write my own is the one way to change that
  // answer. Until it settles it takes no tap or key (ConfirmYesButton's settle
  // guard), unchanged on screen.
  const confirmYes =
    confirmPick && !isAuthorityReconciliation ? (
      <div className={optionsFirst ? "mb-4" : "mt-6"}>
        <ConfirmYesButton
          posting={yesPosting}
          held={yesHeld}
          gated={actsHeld || (submit.isPending && !yesPosting)}
          chosen={yesChosen}
          label={stripAnchors(confirmPick.label)}
          onYes={onYes}
        />
      </div>
    ) : null;
  const answerBlock = (
    <AnswerBlock
      hasOptions={picks.length > 0}
      boxOpen={boxOpen}
      draft={draft}
      onDraftChange={setDraft}
      pending={submit.isPending}
      textareaRef={answerBoxRef}
      status={saved ? SAVED_WORD : <span className={KEY_HINT_INLINE}>{`${shortcut("↵")} to save & continue`}</span>}
      act={
        <>
          {submit.isPending && !yesPosting ? "saving…" : "Save & next"}
          <span className="opacity-60">→</span>
        </>
      }
      onAct={handleSubmit}
      actDisabled={!canSubmit || actsHeld}
    />
  );

  return (
    <div className="animate-slide-up" onClickCapture={() => setKeyActsHeld(false)}>
      {showTitleAsLabel && (
        <p className="text-[12px] font-mono uppercase tracking-widest text-ink-500 mb-2">
          {node.title}
        </p>
      )}

      {/* V2 structured-question: render playback → question → picks →
          hypothesis as one cohesive flow. No section labels — the visual
          hierarchy (paragraph spacing, heading weight, picks visually
          attached to the question) carries the reading order. The founder
          experiences this as a single well-written question with options. */}
      {playback && (
        <p className="text-[15px] text-ink-300 leading-relaxed whitespace-pre-wrap mb-5">
          {playback}
        </p>
      )}

      <h1 className="text-2xl font-medium text-ink-50 tracking-tighter2 leading-snug whitespace-pre-wrap">
        {headingText}
      </h1>

      <WhyLine line={whyLine} outOfScopeEntry={scopeLine} heading={crossingHeading} crossing={crossing.map((c) => c.title)} />

      {/* 0148: the covered-by row renders ONCE per question, by predicate not
          by location - here, above the ordinary card's options and answer box
          (above the answer box on a card with no options), for a
          non-authority question; for an authority question the SOLE renderer
          is the instance inside AuthorityForkPanel. Keyed on the holder, so a
          re-pointed marker remounts the row (its amend draft is per holder).
          0051 S1a: the disabled fieldset holds the row's acts under the mount
          gate (min-w-0 keeps it inside a phone's width). */}
      {optionsFirst && coveredRow}
      {/* 0157 F4: directly above the options and the answer block (or the
          answer block alone), so it sits above the prefilled answer in either
          branch. It wraps rather than widening the card (a long title or
          reason on a phone). */}
      {optionsFirst && reopenedLine}
      {/* The options render below the question (after the covered-by row and
          the reopened line when either shows), with the answer box and Save &
          next directly under them, so the box Edit opens sits next to the
          options on a phone (0051 S1b). Each option shows its whole proposal
          and its cost; one click selects it. */}
      {optionsFirst && (
        <div className="mt-4">
          {confirmYes}
          {decisionAxis && (
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-400">
              Deciding: {decisionAxis}
            </p>
          )}
          <div data-testid="answer-options" className="space-y-2">
            {picks.map((pick) => {
              const selected = draftIsPick(pick);
              return (
                <div key={pick.label}>
                  <AnswerOption
                    pick={pick}
                    selected={selected}
                    disabled={submit.isPending}
                    onSelect={() => {
                      // 0131-T7: the option's text verbatim; Save & next posts a
                      // routing pick's id with it (routingPickOf), and an
                      // ordinary designed-fork pick routes by its text alone.
                      setDraft(pickText(pick));
                      setEditorOpen(false);
                    }}
                  />
                  {selected && !boxOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditorOpen(true);
                        setFocusRequest((n) => n + 1);
                      }}
                      disabled={submit.isPending}
                      className="mt-1 ml-5 text-[13px] text-accent underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      {EDIT_ACT}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {!boxOpen && (
            <button
              type="button"
              onClick={() => {
                setDraft("");
                setEditorOpen(true);
                setFocusRequest((n) => n + 1);
              }}
              disabled={submit.isPending}
              className="mt-3 text-[13px] text-accent underline-offset-2 hover:underline disabled:opacity-50"
            >
              Write my own
            </button>
          )}
        </div>
      )}
      {optionsFirst && answerBlock}

      {flipCondition && (
        <p className="mt-4 text-[13px] text-ink-400 leading-relaxed">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
            What would change this:{' '}
          </span>
          {flipCondition}
        </p>
      )}

      {hypothesis && (
        <p className="mt-6 text-[15px] text-ink-300 leading-relaxed whitespace-pre-wrap">
          {hypothesis}
        </p>
      )}

      {/* the covered-by row, no-option order (see the 0148 comment above) */}
      {!optionsFirst && coveredRow}
      {/* the reopened line, no-option order (see the 0157 F4 comment above) */}
      {!optionsFirst && reopenedLine}

      {isAuthorityReconciliation ? (
        // 0051 S1a: the mount gate holds the fork from outside it - the
        // disabled fieldset holds its rows and Save & next, and its own
        // Cmd/Ctrl+Enter reads the gate through keyActsHeld.
        <fieldset disabled={actsHeld} className="m-0 min-w-0 border-0 p-0">
          <AuthorityForkPanel
            productId={productId}
            node={node}
            dimension={authorityDimension as "scope" | "moat"}
            keepPick={keepPick}
            revisePick={revisePick}
            bothStandPick={bothStandPick}
            bothStandOffered={bothStandOffered && !bothStandHidden}
            isCollision={isCollision}
            onActNext={onActNext}
            coveredHolder={offered}
            hasSavedDraft={hasSavedDraft}
            answerUnderReview={answerUnderReview}
            blocksContinue={blocksContinue}
            handOff={handOff}
            keyActsHeld={keyActsHeld || actsHeld}
            onRefused={onRefused}
          />
        </fieldset>
      ) : (
        !optionsFirst && (
          <>
            {confirmYes}
            {answerBlock}
          </>
        )
      )}

      {/* Every question folds its reasoning and its allowlisted provenance
          (provenanceLines) under a closed Details; a scope question lists its
          crossing decisions once, in its Why block. */}
      {(reasoningChain !== "" || buildsOn.length > 0) && (
        <DetailsFold>
          {reasoningChain !== "" && (
            <div className="border-l-2 border-ink-800 pl-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                Reasoning
              </p>
              <p className="break-words text-[14px] text-ink-300 leading-relaxed whitespace-pre-wrap">
                {reasoningChain}
              </p>
            </div>
          )}
          {buildsOn.length > 0 && (
            <ul data-testid="review-provenance" className="space-y-1 break-words text-[13px] text-ink-300">
              {buildsOn.map((line, i) => (
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          )}
        </DetailsFold>
      )}

      {/* 0159: a refusal (DraftRefusedError) is the page's notice, never this line. */}
      {submit.error && !(submit.error instanceof DraftRefusedError) && (
        <p className="mt-4 text-sm text-danger">
          Couldn't save: {(submit.error as Error).message}
        </p>
      )}

      <NavFooter pos={pos} nothingWaits={nothingWaits} onPrev={onPrev} onNext={onNext} />
    </div>
  );
}
